import { Video, VideoOff, Mic, MicOff, Phone, Monitor, Settings, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState, useEffect, useCallback } from "react";

interface VideoPlaceholderProps {
  participantName: string;
  isLocal?: boolean;
  isConnected?: boolean;
}

export function VideoPlaceholder({ participantName, isLocal = false, isConnected = false }: VideoPlaceholderProps) {
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);

  return (
    <div className={`relative rounded-xl overflow-hidden bg-muted ${isLocal ? "aspect-video" : "aspect-video"}`}>
      {/* Video placeholder area */}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
        {isConnected ? (
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-4xl font-bold text-primary">
                {participantName.charAt(0).toUpperCase()}
              </span>
            </div>
            <p className="text-foreground font-medium">{participantName}</p>
            <p className="text-sm text-muted-foreground">
              {videoEnabled ? "Camera on" : "Camera off"}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <Video className="h-16 w-16 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-muted-foreground">Waiting for {isLocal ? "you to join" : "participant"}...</p>
          </div>
        )}
      </div>

      {/* Connection status indicator */}
      <div className="absolute top-3 left-3">
        <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${
          isConnected 
            ? "bg-green-500/20 text-green-400" 
            : "bg-yellow-500/20 text-yellow-400"
        }`}>
          <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-400" : "bg-yellow-400 animate-pulse"}`} />
          {isConnected ? "Connected" : "Connecting..."}
        </div>
      </div>

      {/* Name overlay */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <span className="bg-black/60 text-white px-3 py-1 rounded-lg text-sm font-medium">
          {participantName} {isLocal && "(You)"}
        </span>
      </div>

      {/* Controls for local video */}
      {isLocal && (
        <div className="absolute bottom-3 right-3 flex gap-2">
          <Button
            size="icon"
            variant={videoEnabled ? "secondary" : "destructive"}
            className="h-8 w-8 rounded-full"
            onClick={() => setVideoEnabled(!videoEnabled)}
          >
            {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          </Button>
          <Button
            size="icon"
            variant={audioEnabled ? "secondary" : "destructive"}
            className="h-8 w-8 rounded-full"
            onClick={() => setAudioEnabled(!audioEnabled)}
          >
            {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}

export interface DeviceSelections {
  microphoneId: string;
  cameraId: string;
  speakerId: string;
}

interface VideoControlsProps {
  onEndCall: () => void;
  onDeviceChange?: (devices: DeviceSelections) => void;
}

export function VideoControls({ onEndCall, onDeviceChange }: VideoControlsProps) {
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);

  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState("default");
  const [selectedCamera, setSelectedCamera] = useState("default");
  const [selectedSpeaker, setSelectedSpeaker] = useState("default");

  const loadDevices = useCallback(async () => {
    try {
      // Request permission to get labels
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((t) => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMicrophones(devices.filter((d) => d.kind === "audioinput"));
      setCameras(devices.filter((d) => d.kind === "videoinput"));
      setSpeakers(devices.filter((d) => d.kind === "audiooutput"));
    } catch {
      // Try without video
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        const devices = await navigator.mediaDevices.enumerateDevices();
        setMicrophones(devices.filter((d) => d.kind === "audioinput"));
        setCameras(devices.filter((d) => d.kind === "videoinput"));
        setSpeakers(devices.filter((d) => d.kind === "audiooutput"));
      } catch {
        // Permission denied
      }
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    onDeviceChange?.({ microphoneId: selectedMic, cameraId: selectedCamera, speakerId: selectedSpeaker });
  }, [selectedMic, selectedCamera, selectedSpeaker, onDeviceChange]);

  return (
    <div className="flex items-center justify-center gap-3 p-4 bg-card border-t border-border">
      <Button
        size="lg"
        variant={audioEnabled ? "outline" : "destructive"}
        className="rounded-full h-12 w-12"
        onClick={() => setAudioEnabled(!audioEnabled)}
      >
        {audioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
      </Button>
      
      <Button
        size="lg"
        variant={videoEnabled ? "outline" : "destructive"}
        className="rounded-full h-12 w-12"
        onClick={() => setVideoEnabled(!videoEnabled)}
      >
        {videoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
      </Button>

      <Button
        size="lg"
        variant={screenSharing ? "default" : "outline"}
        className="rounded-full h-12 w-12"
        onClick={() => setScreenSharing(!screenSharing)}
      >
        <Monitor className="h-5 w-5" />
      </Button>

      {/* Settings Popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            size="lg"
            variant="outline"
            className="rounded-full h-12 w-12"
          >
            <Settings className="h-5 w-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="top" align="center">
          <div className="space-y-4">
            <h4 className="font-semibold text-sm">Device Settings</h4>

            {/* Microphone */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5" /> Microphone
              </Label>
              <Select value={selectedMic} onValueChange={setSelectedMic}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select microphone" />
                </SelectTrigger>
                <SelectContent>
                  {microphones.map((d) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone ${d.deviceId.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                  {microphones.length === 0 && (
                    <SelectItem value="none" disabled>No microphones found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Camera */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Video className="h-3.5 w-3.5" /> Camera
              </Label>
              <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select camera" />
                </SelectTrigger>
                <SelectContent>
                  {cameras.map((d) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                  {cameras.length === 0 && (
                    <SelectItem value="none" disabled>No cameras found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Speaker */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Volume2 className="h-3.5 w-3.5" /> Speaker
              </Label>
              <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select speaker" />
                </SelectTrigger>
                <SelectContent>
                  {speakers.map((d) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `Speaker ${d.deviceId.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                  {speakers.length === 0 && (
                    <SelectItem value="none" disabled>No speakers found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        size="lg"
        variant="destructive"
        className="rounded-full h-12 px-6 gap-2"
        onClick={onEndCall}
      >
        <Phone className="h-5 w-5 rotate-[135deg]" />
        End Call
      </Button>
    </div>
  );
}
